<script setup>
import { ref, reactive, onMounted } from "vue";
import { gql } from "@apollo/client/core";
import apolloClient from "@/apollo";

// GraphQL Mutations
const CREATE_ACCOMODATION = gql`
  mutation CreateAccomodation($createAccomodationInput: CreateAccomodationInput!) {
    createAccomodation(createAccomodationInput: $createAccomodationInput) {
      id
      name
    }
  }
`;

const UPDATE_ACCOMODATION = gql`
  mutation UpdateAccomodationById($id: String!, $updateAccomodationInput: UpdateAccomodationInput!) {
    updateAccomodationById(id: $id, updateAccomodationInput: $updateAccomodationInput) {
      status
      message
      data {
        id
        name
        city
        address
        region
      }
    }
  }
`;

const REMOVE_ACCOMODATION = gql`
  mutation RemoveAccomodationById($id: String!) {
    removeAccomodationById(id: $id) {
      status
      message
    }
  }
`;

const CREATE_EQUIPMENT = gql`
  mutation CreateEquipment($createEquipmentInput: CreateEquipmentInput!) {
    createEquipment(createEquipmentInput: $createEquipmentInput) {
      id
      name
      quantity
      description
      available
    }
  }
`;

const REMOVE_EQUIPMENT = gql`
  mutation RemoveEquipmentById($id: Int!) {
    removeEquipmentById(id: $id) {
      status
      message
    }
  }
`;

const REMOVE_ACCOMODATION_PHOTO = gql`
  mutation RemoveAccomodationPhotoById($id: ID!) {
    removeAccomodationPhotoById(id: $id) {
      status
      message
    }
  }
`;

// Function to get cookie value
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
}

const GET_ACCOMODATION_BY_ID = gql`
  query GetAccomodationById($id: ID!) {
    getAccomodationById(id: $id) {
      data {
        id
        name
        address
        description
        city
        region
        capacity
        roomQty
        wcQty
        bedsQty
        rate
        type
        equipments {
          name
          description
          icon
          available
          quantity
        }
        tags {
          label
        }
        price
        accomodationPhotos {
          id
          photoUrl
        }
      }
    }
  }
`;

// GraphQL Query to get accommodations
const GET_ACCOMODATIONS = gql`
  query GetAccommodations {
    getAccomodations {
      message
      data {
        id
        name
        address
        description
        city
        region
        capacity
        roomQty
        wcQty
        bedsQty
        rate
        type
        equipments {
          name
          description
          icon
          available
          quantity
        }
        tags {
          label
        }
        accomodationPhotos {
          id
          photoUrl
        }
        price
      }
    }
  }
`;
 

// Reactive data for accommodations
const maisons = ref([]);

// Fetch accommodations
const fetchAccommodations = async () => {
  try {
    const { data } = await apolloClient.query({
      query: GET_ACCOMODATIONS,
      fetchPolicy: 'no-cache',
    });
    if (data?.getAccomodations?.data) {
      maisons.value = data.getAccomodations.data.filter(acc => acc.type === "GUESTHOUSE").map(acc => ({
        id: acc.id,
        nom: acc.name,
        ville: acc.city,
        region: acc.region,
        capacite: acc.capacity,
        price: acc.price || 0,
        statut: "Disponible", // Assuming all are available for now
        description: acc.description,
        image: "../../assets/img/team-2.jpg", // Default image
        equipements: acc.equipements?.map(eq => ({ id: eq.id, name: eq.name, quantity: eq.quantity })) || [],
        images: acc.accomodationPhotos?.map(photo => ({ id: photo.id, photoUrl: photo.photoUrl })) || []
      }));
    }
  } catch (error) {
    console.error("Error fetching accommodations:", error);
  }
};

const showModal = ref(false);
const showEquipementsModal = ref(false);
const showImagesModal = ref(false);
const showDetailsModal = ref(false);
const showReservationsModal = ref(false);
const isEditing = ref(false);
const currentMaison = reactive({
  id: null,
  nom: "",
  ville: "",
  region: "",
  capacite: 1,
  price: 0,
  statut: "Disponible",
  description: "",
  image: "../../assets/img/team-2.jpg",
  equipements: [],
  images: [],
  // Additional fields for API
  address: "",
  roomQty: 1,
  wcQty: 1,
  bedsQty: 1,
  rate: 5,
  type: "GUESTHOUSE",
  tags: [],
  calendarAvailabilities: {
    date: "",
    isAvailable: true,
    price: 0,
    minStay: 1,
    maxStay: 30,
    type: "AVAILABLE"
  },
  tagsInput: "" // for input
});

const selectedMaisonForEquipements = ref(null);
const selectedMaisonForImages = ref(null);
const selectedMaisonForDetails = ref(null);
const selectedMaisonForReservations = ref(null);
const currentEquipements = ref([]);
const newEquipement = ref("");
const newEquipementDescription = ref("");
const newEquipementIcon = ref("fa-icon");
const newEquipementAvailable = ref(true);
const newEquipementQuantity = ref(1);
const selectedFile = ref(null);


const openAddModal = () => {
  isEditing.value = false;
  Object.assign(currentMaison, {
    id: null,
    nom: "",
    ville: "",
    region: "",
    capacite: 1,
    price: 0,
    statut: "Disponible",
    description: "",
    image: "../../assets/img/team-2.jpg",
    equipements: [],
    images: [],
    address: "",
    roomQty: 1,
    wcQty: 1,
    bedsQty: 1,
    rate: 5,
    type: "GUESTHOUSE",
    tags: [],
    calendarAvailabilities: {
      date: "",
      isAvailable: true,
      price: 0,
      minStay: 1,
      maxStay: 30,
      type: "AVAILABLE"
    },
    tagsInput: ""
  });
  showModal.value = true;
};

const openImagesModal = (maison) => {
  selectedMaisonForImages.value = maison;
  showImagesModal.value = true;
};

const openEquipementsModal = async (maison) => {
  selectedMaisonForEquipements.value = maison;
  const { data } = await apolloClient.query({
    query: GET_ACCOMODATION_BY_ID,
    variables: { id: maison.id },
    fetchPolicy: 'no-cache',
  });
  if (data?.getAccomodationById?.data) {
    currentEquipements.value = data.getAccomodationById.data.equipments || [];
  } else {
    currentEquipements.value = [];
  }
  showEquipementsModal.value = true;
};

const openDetailsModal = async (maison) => {
  try {
    const { data } = await apolloClient.query({
      query: GET_ACCOMODATION_BY_ID,
      variables: { id: maison.id },
      fetchPolicy: 'no-cache',
    });
    if (data?.getAccomodationById?.data) {
      selectedMaisonForDetails.value = data.getAccomodationById.data;
      showDetailsModal.value = true;
    }
  } catch (error) {
    console.error("Error fetching accommodation details:", error);
  }
};

const openReservationsModal = async (maison) => {
  try {
    const { data } = await apolloClient.query({
      query: GET_ACCOMODATION_BY_ID,
      variables: { id: maison.id },
      fetchPolicy: 'no-cache',
    });
    if (data?.getAccomodationById?.data) {
      selectedMaisonForReservations.value = data.getAccomodationById.data;
      showReservationsModal.value = true;
    }
  } catch (error) {
    console.error("Error fetching accommodation reservations:", error);
  }
};

const openEditModal = (maison) => {
  isEditing.value = true;
  Object.assign(currentMaison, { ...maison });
  showModal.value = true;
};

const saveMaison = async () => {
  const input = {
    name: currentMaison.nom,
    address: currentMaison.address,
    description: currentMaison.description,
    city: currentMaison.ville,
    region: currentMaison.region.toUpperCase().replace(' ', '_'),
    capacity: currentMaison.capacite,
    roomQty: currentMaison.roomQty,
    wcQty: currentMaison.wcQty,
    bedsQty: currentMaison.bedsQty,
    rate: currentMaison.rate,
    type: currentMaison.type,
    price: currentMaison.price,
    calendarAvailabilities: currentMaison.calendarAvailabilities
    };

  if (isEditing.value) {
    // Remove prix from input for update
    const updateInput = { ...input };
    delete updateInput.price;

    // Call the update mutation
    try {
      const { data } = await apolloClient.mutate({
        mutation: UPDATE_ACCOMODATION,
        variables: { id: currentMaison.id, updateAccomodationInput: updateInput },
        context: {
          headers: {
            authorization: getCookie('accessToken') ? `Bearer ${getCookie('accessToken')}` : "",
          }
        }
      });

      if (data?.updateAccomodationById) {
        // Refresh the list
        await fetchAccommodations();
      }
    } catch (error) {
      console.error("Error updating accommodation:", error);
      alert("Erreur lors de la modification de l'hébergement");
      return;
    }
  } else {
    // Call the create mutation
    try {
      const { data } = await apolloClient.mutate({
        mutation: CREATE_ACCOMODATION,
        variables: { createAccomodationInput: input },
        context: {
          headers: {
            authorization: getCookie('accessToken') ? `Bearer ${getCookie('accessToken')}` : "",
          }
        }
      });

      if (data?.createAccomodation) {
        // Refresh the list
        await fetchAccommodations();
      }
    } catch (error) {
      console.error("Error creating accommodation:", error);
      alert("Erreur lors de la création de l'hébergement");
      return;
    }
  }
  showModal.value = false;
};

const deleteMaison = async (id) => {
  if (confirm("Êtes-vous sûr de vouloir supprimer cette maison d'hôtes ?")) {
    try {
      const { data } = await apolloClient.mutate({
        mutation: REMOVE_ACCOMODATION,
        variables: { id },
        context: {
          headers: {
            authorization: getCookie('accessToken') ? `Bearer ${getCookie('accessToken')}` : "",
          }
        }
      });

      if (data?.removeAccomodationById) {
        // Remove from local array
        maisons.value = maisons.value.filter(m => m.id !== id);
        alert("Hébergement supprimé avec succès");
      } else {
        alert("Erreur lors de la suppression");
      }
    } catch (error) {
      console.error("Error deleting accommodation:", error);
      alert("Erreur lors de la suppression de l'hébergement");
    }
  }
};

const closeModal = () => {
  showModal.value = false;
};

const closeEquipementsModal = () => {
  showEquipementsModal.value = false;
  selectedMaisonForEquipements.value = null;
  currentEquipements.value = [];
};

const closeImagesModal = () => {
  showImagesModal.value = false;
  selectedMaisonForImages.value = null;
  selectedFile.value = null;
};

const closeDetailsModal = () => {
  showDetailsModal.value = false;
  selectedMaisonForDetails.value = null;
};

const closeReservationsModal = () => {
  showReservationsModal.value = false;
  selectedMaisonForReservations.value = null;
};

const addEquipement = async () => {
  if (newEquipement.value.trim() && selectedMaisonForEquipements.value) {
    try {
      const { data } = await apolloClient.mutate({
        mutation: CREATE_EQUIPMENT,
        variables: {
          createEquipmentInput: {
            name: newEquipement.value.trim(),
            description: newEquipementDescription.value,
            accomodationsIds: [selectedMaisonForEquipements.value.id],
            icon: newEquipementIcon.value,
            available: newEquipementAvailable.value,
            quantity: newEquipementQuantity.value
          }
        },
        context: {
          headers: {
            authorization: getCookie('accessToken') ? `Bearer ${getCookie('accessToken')}` : "",
          }
        }
      });

      if (data?.createEquipment) {
        // Add to local array if not already present
        if (!selectedMaisonForEquipements.value.equipements.some(e => e.name === newEquipement.value.trim())) {
          selectedMaisonForEquipements.value.equipements.push({ id: data.createEquipment.id, name: newEquipement.value.trim(), quantity: newEquipementQuantity.value });
        }
        // Add to currentEquipements for modal display
        if (!currentEquipements.value.some(e => e.name === newEquipement.value.trim())) {
          currentEquipements.value.push({ id: data.createEquipment.id, name: newEquipement.value.trim(), quantity: newEquipementQuantity.value });
        }
        // Reset form
        newEquipement.value = "";
        newEquipementDescription.value = "";
        newEquipementIcon.value = "fa-icon";
        newEquipementAvailable.value = true;
        newEquipementQuantity.value = 1;
      }
    } catch (error) {
      console.error("Error creating equipment:", error);
      alert("Erreur lors de l'ajout de l'équipement");
    }
  }
};


const removeEquipement = async (equipementId) => {
  if (confirm("Êtes-vous sûr de vouloir supprimer cet équipement ?")) {
    try {
      const { data } = await apolloClient.mutate({
        mutation: REMOVE_EQUIPMENT,
        variables: { id: equipementId },
        context: {
          headers: {
            authorization: getCookie('accessToken') ? `Bearer ${getCookie('accessToken')}` : "",
          }
        }
      });

      if (data?.removeEquipmentById) {
        // Remove from currentEquipements
        currentEquipements.value = currentEquipements.value.filter(e => e.id !== equipementId);
        // Refresh the accommodation data
        const { data: accData } = await apolloClient.query({
          query: GET_ACCOMODATION_BY_ID,
          variables: { id: selectedMaisonForEquipements.value.id },
          fetchPolicy: 'no-cache',
        });
        if (accData?.getAccomodationById?.data) {
          selectedMaisonForEquipements.value.equipements = accData.getAccomodationById.data.equipements?.map(eq => ({ id: eq.id, name: eq.name, quantity: eq.quantity })) || [];
          currentEquipements.value = accData.getAccomodationById.data.equipements?.map(eq => ({ id: eq.id, name: eq.name, quantity: eq.quantity })) || [];
        }
        alert("Équipement supprimé avec succès");
      } else {
        alert("Erreur lors de la suppression");
      }
    } catch (error) {
      console.error("Error removing equipment:", error);
      if (error.message.includes("foreign key constraint")) {
        alert("Impossible de supprimer l'équipement car il est encore associé à des hébergements");
      } else {
        alert("Erreur lors de la suppression de l'équipement");
      }
    }
  }
};

 

const addImage = async () => {
  if (!selectedFile.value) {
    alert("Veuillez sélectionner un fichier");
    return;
  }
  if (selectedMaisonForImages.value) {
    try {
      const formData = new FormData();
      formData.append('operations', JSON.stringify({
        query: `mutation uploadImage($id: String!, $accomodation: String!, $file: Upload!) {
          uploadImage(id: $id, accomodation: $accomodation, file: $file) {
            status
            data {
              id
              photoUrl
            }
          }
        }`,
        variables: {
          id: selectedMaisonForImages.value.id,
          accomodation: selectedMaisonForImages.value.nom,
          file: null
        }
      }));
      formData.append('map', JSON.stringify({ '0': ['variables.file'] }));
      formData.append('0', selectedFile.value);

      const response = await fetch(process.env.VUE_APP_GRAPHQL_ENDPOINT || 'https://hs-travel-platform-be-production.up.railway.app/graphql', {
        method: 'POST',
        headers: {
          authorization: getCookie('accessToken') ? `Bearer ${getCookie('accessToken')}` : "",
        },
        body: formData
      });

      const result = await response.json();
      if (result?.data?.uploadImage?.data?.photoUrl) {
        const newImage = { id: result.data.uploadImage.data.id, photoUrl: result.data.uploadImage.data.photoUrl };
        if (!selectedMaisonForImages.value.images.some(img => img.photoUrl === newImage.photoUrl)) {
          selectedMaisonForImages.value.images.push(newImage);
        }
      }
      selectedFile.value = null;
    } catch (error) {
      console.error("Error uploading image:", error);
      alert("Erreur lors de l'ajout de l'image");
    }
  }
};

const removeImage = async (index) => {
  if (selectedMaisonForImages.value && selectedMaisonForImages.value.images[index]) {
    const imageId = selectedMaisonForImages.value.images[index].id;
    try {
      const { data } = await apolloClient.mutate({
        mutation: REMOVE_ACCOMODATION_PHOTO,
        variables: { id: imageId },
        context: {
          headers: {
            authorization: getCookie('accessToken') ? `Bearer ${getCookie('accessToken')}` : "",
          }
        }
      });

      if (data?.removeAccomodationPhotoById) {
        selectedMaisonForImages.value.images.splice(index, 1);
        alert("Image supprimée avec succès");
      } else {
        alert("Erreur lors de la suppression de l'image");
      }
    } catch (error) {
      console.error("Error removing image:", error);
      alert("Erreur lors de la suppression de l'image");
    }
  }
};

const handleFileSelect = (event) => {
  selectedFile.value = event.target.files[0];
};

const setAsMainImage = (imageUrl) => {
  if (selectedMaisonForImages.value) {
    selectedMaisonForImages.value.image = imageUrl;
  }
};

onMounted(() => {
  fetchAccommodations();
});
</script>
<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0 d-flex justify-content-between align-items-center">
            <h6>Gérer vos maisons d'hôtes</h6>
            <button class="btn btn-primary btn-sm" @click="openAddModal">
              <i class="ni ni-fat-add"></i> Ajouter
            </button>
          </div>
          <div class="card-body px-0 pt-0 pb-2">
            <div class="table-responsive p-0">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Maison d'hôte
                    </th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                      Localisation
                    </th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Statut
                    </th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Capacité
                    </th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Prix (DT/nuit)
                    </th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Équipements
                    </th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Images
                    </th>
                    <th class="text-secondary opacity-7">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="maison in maisons" :key="maison.id">
                    <td>
                      <div class="d-flex px-2 py-1">
                         
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-sm">{{ maison.nom }}</h6>
                          <p class="text-xs text-secondary mb-0">
                            {{ maison.description }}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td>
                      <p class="text-xs font-weight-bold mb-0">{{ maison.ville }}</p>
                      <p class="text-xs text-secondary mb-0">{{ maison.region }}</p>
                    </td>
                    <td class="align-middle text-center text-sm">
                      <span
                        class="badge badge-sm"
                        :class="maison.statut === 'Disponible' ? 'bg-gradient-success' : 'bg-gradient-warning'"
                        style="cursor: pointer;"
                        @click="openReservationsModal(maison)"
                      >
                        {{ maison.statut }}
                      </span>
                    </td>
                    <td class="align-middle text-center">
                      <span class="text-secondary text-xs font-weight-bold">
                        {{ maison.capacite }} personnes
                      </span>
                    </td>
                    <td class="align-middle text-center">
                      <span class="text-secondary text-xs font-weight-bold">
                        {{ maison.price }} DT
                      </span>
                    </td>
                    <td class="align-middle text-center">
                      <button
                        class="btn btn-link text-warning mb-0"
                        @click="openEquipementsModal(maison)"
                        :title="'Gérer les équipements (' + maison.equipements.length + ')'"
                      >
                        <i class="fa fa-cogs text-xs"></i>
                        <span class="badge badge-sm bg-gradient-secondary ms-1">{{ maison.equipements.length }}</span>
                      </button>
                    </td>
                    <td class="align-middle text-center">
                      <button
                        class="btn btn-link text-success mb-0"
                        @click="openImagesModal(maison)"
                        :title="'Gérer les images (' + maison.images.length + ')'"
                      >
                        <i class="fa fa-images text-xs"></i>
                        <span class="badge badge-sm bg-gradient-primary ms-1">{{ maison.images.length }}</span>
                      </button>
                    </td>
                    <td class="align-middle">
                      <button
                        class="btn btn-link text-primary mb-0 me-2"
                        @click="openDetailsModal(maison)"
                        title="Voir les détails"
                      >
                        <i class="fa fa-eye text-xs"></i>
                      </button>
                      <button
                        class="btn btn-link text-info mb-0 me-2"
                        @click="openEditModal(maison)"
                      >
                        <i class="fa fa-edit text-xs"></i>
                      </button>
                      <button
                        class="btn btn-link text-danger mb-0"
                        @click="deleteMaison(maison.id)"
                      >
                        <i class="fa fa-trash text-xs"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal for Equipment Management -->
    <div v-if="showEquipementsModal" class="modal fade show d-block" style="background-color: rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Gestion des Équipements - {{ selectedMaisonForEquipements?.nom }}
            </h5>
            <button type="button" class="btn-close" @click="closeEquipementsModal"></button>
          </div>
          <div class="modal-body">
            <div class="row">
              <div class="col-md-6">
                <h6>Ajouter un équipement</h6>
                <div class="mb-3">
                  <input
                    v-model="newEquipement"
                    type="text"
                    class="form-control"
                    placeholder="Nom de l'équipement"
                  />
                </div>
                <div class="mb-3">
                  <input
                    v-model="newEquipementDescription"
                    type="text"
                    class="form-control"
                    placeholder="Description"
                  />
                </div>
                <div class="mb-3">
                  <input
                    v-model="newEquipementIcon"
                    type="text"
                    class="form-control"
                    placeholder="Icône (ex: fa-icon)"
                  />
                </div>
                <div class="mb-3">
                  <div class="form-check">
                    <input
                      v-model="newEquipementAvailable"
                      class="form-check-input"
                      type="checkbox"
                      id="availableCheck"
                    />
                    <label class="form-check-label" for="availableCheck">
                      Disponible
                    </label>
                  </div>
                </div>
                <div class="mb-3">
                  <input
                    v-model.number="newEquipementQuantity"
                    type="number"
                    class="form-control"
                    placeholder="Quantité"
                    min="1"
                  />
                </div>
                <button class="btn btn-outline-primary" @click="addEquipement">
                  Ajouter
                </button>
                <h6>Équipements de cette Maison d'hote</h6>
                <div v-if="currentEquipements.length" class="d-flex flex-wrap gap-2">
                  <div v-for="equip in currentEquipements" :key="equip.id" class="d-flex align-items-center gap-2 p-2 border rounded">
                    <span>{{ equip.name }} ({{ equip.quantity }})</span>
                    <button class="btn btn-sm btn-outline-danger" @click="removeEquipement(equip.id)">
                      <i class="fa fa-trash"></i>
                    </button>
                  </div>
                </div>
                <div v-else>
                  <p>Aucun équipement</p>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeEquipementsModal">
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal for Images Management -->
    <div v-if="showImagesModal" class="modal fade show d-block" style="background-color: rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Gestion des Images - {{ selectedMaisonForImages?.nom }}
            </h5>
            <button type="button" class="btn-close" @click="closeImagesModal"></button>
          </div>
          <div class="modal-body">
            <div class="row">
              <div class="col-md-8">
                <h6>Images actuelles</h6>
                <div class="row g-3">
                  <div
                    v-for="(image, index) in selectedMaisonForImages?.images"
                    :key="index"
                    class="col-md-4"
                  >
                    <div class="card">
                      <img :src="image.photoUrl" class="card-img-top" style="height: 150px; object-fit: cover;" />
                      <div class="card-body p-2">
                        <div class="d-flex justify-content-between align-items-center">
                          <small class="text-muted">Image {{ index + 1 }}</small>
                          <div>
                            <button
                              v-if="selectedMaisonForImages?.image !== image.photoUrl"
                              class="btn btn-sm btn-outline-primary me-1"
                              @click="setAsMainImage(image.photoUrl)"
                              title="Définir comme image principale"
                            >
                              <i class="fa fa-star"></i>
                            </button>
                            <button
                              v-else
                              class="btn btn-sm btn-primary me-1"
                              disabled
                              title="Image principale"
                            >
                              <i class="fa fa-star"></i>
                            </button>
                            <button
                              class="btn btn-sm btn-outline-danger"
                              @click="removeImage(index)"
                            >
                              <i class="fa fa-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-if="!selectedMaisonForImages?.images.length" class="col-12">
                    <div class="text-center text-muted py-5">
                      <i class="fa fa-images fa-3x mb-3"></i>
                      <p>Aucune image</p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <h6>Ajouter une image</h6>
                <div class="input-group mb-3">
                  <input
                    type="file"
                    @change="handleFileSelect"
                    class="form-control"
                    accept="image/*"
                  />
                  <button class="btn btn-outline-primary" @click="addImage">
                    Télécharger
                  </button>
                </div>
                <div class="alert alert-info">
                  <i class="fa fa-info-circle"></i>
                  <small class="ms-2">
                    Téléchargez des images pour la galerie de la maison d'hôtes.
                    L'image marquée d'une étoile sera l'image principale affichée aux clients.
                  </small>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeImagesModal">
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal for Add/Edit -->
    <div v-if="showModal" class="modal fade show d-block" style="background-color: rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ isEditing ? 'Modifier' : 'Ajouter' }} une Maison d'hôtes
            </h5>
            <button type="button" class="btn-close" @click="closeModal"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveMaison">
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Nom</label>
                    <input
                      v-model="currentMaison.nom"
                      type="text"
                      class="form-control"
                      required
                    />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Adresse</label>
                    <input
                      v-model="currentMaison.address"
                      type="text"
                      class="form-control"
                      required
                    />
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Ville</label>
                    <input
                      v-model="currentMaison.ville"
                      type="text"
                      class="form-control"
                      required
                    />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Type</label>
                    <select v-model="currentMaison.type" class="form-control" required>
                      <option value="GUESTHOUSE">Maison d'hôtes</option>
                    </select>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Région</label>
                    <select v-model="currentMaison.region" class="form-control" required>
                      <option value="">Sélectionner une région</option>
                      <option value="TUNIS">Tunis</option>
                      <option value="ARIANA">Ariana</option>
                      <option value="BEN_AROUS">Ben Arous</option>
                      <option value="MANOUBA">La Manouba</option>
                      <option value="NABEUL">Nabeul</option>
                      <option value="ZAGHOUAN">Zaghouan</option>
                      <option value="BIZERTE">Bizerte</option>
                      <option value="BEJA">Beja</option>
                      <option value="JENDOUBA">Jendouba</option>
                      <option value="LE_KEF">Le Kef</option>
                      <option value="SILIANA">Siliana</option>
                      <option value="SOUSSE">Sousse</option>
                      <option value="MONASTIR">Monastir</option>
                      <option value="MAHDIA">Mahdia</option>
                      <option value="SFAX">Sfax</option>
                      <option value="KAIROUAN">Kairouan</option>
                      <option value="KASSERINE">Kasserine</option>
                      <option value="SIDI_BOUZID">Sidi Bouzid</option>
                      <option value="GABES">Gabes</option>
                      <option value="MEDENINE">Medenine</option>
                      <option value="TATAOUINE">Tataouine</option>
                      <option value="KEBILI">Kebili</option>
                      <option value="TOZEUR">Tozeur</option>
                      <option value="GAFSA">Gafsa</option>
                    </select>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Capacité (personnes)</label>
                    <input
                      v-model.number="currentMaison.capacite"
                      type="number"
                      class="form-control"
                      min="1"
                      required
                    />
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Nombre de chambres</label>
                    <input
                      v-model.number="currentMaison.roomQty"
                      type="number"
                      class="form-control"
                      min="1"
                      required
                    />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Nombre de WC</label>
                    <input
                      v-model.number="currentMaison.wcQty"
                      type="number"
                      class="form-control"
                      min="1"
                      required
                    />
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Nombre de lits</label>
                    <input
                      v-model.number="currentMaison.bedsQty"
                      type="number"
                      class="form-control"
                      min="1"
                      required
                    />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Note (1-5)</label>
                    <input
                      v-model.number="currentMaison.rate"
                      type="number"
                      class="form-control"
                      min="1"
                      max="5"
                      required
                    />
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Prix (DT/nuit)</label>
                    <input
                      v-model.number="currentMaison.price"
                      type="number"
                      class="form-control"
                      min="0"
                    />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Statut</label>
                    <select v-model="currentMaison.statut" class="form-control" required>
                      <option value="AVAILABLE">Disponible</option>
                      <option value="BOOKED">Réservé</option>
                      <option value="BLOCKED_BY_HOST">En maintenance</option>
                    </select>
                  </div>
                </div>
              </div>
              <div class="form-group">
                <label>Tags (séparés par des virgules)</label>
                <input
                  v-model="currentMaison.tagsInput"
                  type="text"
                  class="form-control"
                  placeholder="Ex: Comfort, Vue mer, Piscine"
                />
              </div>
              <div class="form-group">
                <label>Description</label>
                <textarea
                  v-model="currentMaison.description"
                  class="form-control"
                  rows="3"
                  required
                ></textarea>
              </div>
              <h6>Disponibilités Calendrier</h6>
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Date</label>
                    <input
                      v-model="currentMaison.calendarAvailabilities.date"
                      type="date"
                      class="form-control"
                      required
                    />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Prix</label>
                    <input
                      v-model.number="currentMaison.calendarAvailabilities.price"
                      type="number"
                      step="0.01"
                      class="form-control"
                      min="0"
                      required
                    />
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Séjour minimum (nuits)</label>
                    <input
                      v-model.number="currentMaison.calendarAvailabilities.minStay"
                      type="number"
                      class="form-control"
                      min="1"
                      required
                    />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Séjour maximum (nuits)</label>
                    <input
                      v-model.number="currentMaison.calendarAvailabilities.maxStay"
                      type="number"
                      class="form-control"
                      min="1"
                      required
                    />
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Type</label>
                    <select v-model="currentMaison.calendarAvailabilities.type" class="form-control" required>
                      <option value="AVAILABLE">Disponible</option>
                      <option value="BLOCKED">Bloqué</option>
                    </select>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Disponible</label>
                    <div class="form-check">
                      <input
                        v-model="currentMaison.calendarAvailabilities.isAvailable"
                        class="form-check-input"
                        type="checkbox"
                        id="isAvailableCheck"
                      />
                      <label class="form-check-label" for="isAvailableCheck">
                        Disponible
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">
              Annuler
            </button>
            <button type="button" class="btn btn-primary" @click="saveMaison">
              {{ isEditing ? 'Modifier' : 'Ajouter' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal for Details -->
    <div v-if="showDetailsModal" class="modal fade show d-block" style="background-color: rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Détails de l'hébergement - {{ selectedMaisonForDetails?.name }}
            </h5>
            <button type="button" class="btn-close" @click="closeDetailsModal"></button>
          </div>
          <div class="modal-body" v-if="selectedMaisonForDetails">
            <div class="row">
              <div class="col-md-6">
                <h6>Informations générales</h6>
                <p><strong>Nom:</strong> {{ selectedMaisonForDetails.name }}</p>
                <p><strong>Adresse:</strong> {{ selectedMaisonForDetails.address }}</p>
                <p><strong>Ville:</strong> {{ selectedMaisonForDetails.city }}</p>
                <p><strong>Région:</strong> {{ selectedMaisonForDetails.region }}</p>
                <p><strong>Type:</strong> {{ selectedMaisonForDetails.type }}</p>
                <p><strong>Description:</strong> {{ selectedMaisonForDetails.description }}</p>
              </div>
              <div class="col-md-6">
                <h6>Capacités et équipements</h6>
                <p><strong>Capacité:</strong> {{ selectedMaisonForDetails.capacity }} personnes</p>
                <p><strong>Chambres:</strong> {{ selectedMaisonForDetails.roomQty }}</p>
                <p><strong>WC:</strong> {{ selectedMaisonForDetails.wcQty }}</p>
                <p><strong>Lits:</strong> {{ selectedMaisonForDetails.bedsQty }}</p>
                <p><strong>Note:</strong> {{ selectedMaisonForDetails.rate }}/5</p>
              </div>
            </div>
            <div class="row mt-3">
              <div class="col-md-6">
                <h6>Équipements</h6>
                <ul v-if="selectedMaisonForDetails.equipments && selectedMaisonForDetails.equipments.length">
                  <li v-for="equip in selectedMaisonForDetails.equipments" :key="equip.name">
                    {{ equip.name }} ({{ equip.quantity }})
                  </li>
                </ul>
                <p v-else>Aucun équipement</p>
              </div>
              <div class="col-md-6">
                <h6>Tags</h6>
                <div v-if="selectedMaisonForDetails.tags && selectedMaisonForDetails.tags.length">
                  <span
                    v-for="tag in selectedMaisonForDetails.tags"
                    :key="tag.label"
                    class="badge badge-sm bg-primary me-1"
                  >
                    {{ tag.label }}
                  </span>
                </div>
                <p v-else>Aucun tag</p>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeDetailsModal">
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal for Reservations -->
    <div v-if="showReservationsModal" class="modal fade show d-block" style="background-color: rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Réservations - {{ selectedMaisonForReservations?.name }}
            </h5>
            <button type="button" class="btn-close" @click="closeReservationsModal"></button>
          </div>
          <div class="modal-body" v-if="selectedMaisonForReservations">
            <p><strong>Total:</strong> {{ selectedMaisonForReservations.total }}</p>
            <p><strong>Pages totales:</strong> {{ selectedMaisonForReservations.totalPages }}</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeReservationsModal">
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
